import React from 'react'
import { RemoveScroll } from 'react-remove-scroll'
import { isNumber, mergeClassnames, runIfFn } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'
import { Block } from '../block/block'
import { CloseButton } from '../closeButton'
import { FocusTrap } from '../focusTrap'
import { Portal } from '../portal'
import { DialogProvider, useDialog } from './dialog.context'
import {
  DIALOG_TRANSITION_DURATION,
  useDialogFunctions
} from './useDialogFunctions'

export const DialogContainer = React.forwardRef((props, ref) => {
  const { className, children, containerProps: rootProps, ...rest } = props

  const {
    getDialogProps,
    getDialogContainerProps,
    setContainerMounted,
    shouldRender
  } = useDialog()

  const dialogProps = getDialogProps(rest, ref)
  const containerProps = getDialogContainerProps(rootProps)

  const Wrapper = React.useMemo(
    () =>
      dialogProps.isDrawer
        ? { Root: DrawerRoot, Container: DrawerContainerRoot }
        : { Root: DialogRoot, Container: DialogContainerRoot },
    [dialogProps.isDrawer]
  )

  const _className = mergeClassnames('mote-dialog', className)

  React.useEffect(() => {
    setContainerMounted(true)

    return () => {
      setContainerMounted(false)
    }
  }, [])

  return (
    <DialogFocusScope>
      <Wrapper.Container
        {...containerProps}
        className='mote-dialog__container'
        tabIndex={-1}
      >
        <Wrapper.Root
          className={_className}
          {...dialogProps}
          render={shouldRender}
        >
          {children}
        </Wrapper.Root>
      </Wrapper.Container>
    </DialogFocusScope>
  )
})

if (__DEV__) {
  DialogContainer.displayName = 'DialogContainer'
}

export function DialogFocusScope(props) {
  const {
    autoFocus,
    focusGroup,
    trapFocus,
    dialogRef,
    initialFocusRef,
    blockScrollOnMount,
    allowPinchZoom,
    finalFocusRef,
    returnFocusOnClose,
    preserveScrollBarGap,
    lockFocusAcrossFrames
  } = useDialog()

  return (
    <FocusTrap
      group={focusGroup}
      autoFocus={autoFocus}
      isDisabled={!trapFocus}
      initialFocusRef={initialFocusRef}
      finalFocusRef={finalFocusRef}
      restoreFocus={returnFocusOnClose}
      contentRef={dialogRef}
      lockFocusAcrossFrames={lockFocusAcrossFrames}
    >
      <RemoveScroll
        removeScrollBar={!preserveScrollBarGap}
        allowPinchZoom={allowPinchZoom}
        enabled={blockScrollOnMount}
        forwardProps
      >
        {props.children}
      </RemoveScroll>
    </FocusTrap>
  )
}

export const DialogOverlay = React.forwardRef((props, ref) => {
  const { className, showOverlay = true, isOpen = false, ...rest } = props
  const [isMounted, setIsMounted] = React.useState(false)
  const { isClosing, isOpen: isOpenInternal } = useDialog()
  const _className = mergeClassnames('mote-dialog__overlay', className)

  React.useEffect(() => {
    setIsMounted(true)

    return () => {
      setIsMounted(false)
    }
  }, [])

  return (
    <DialogOverlayRoot
      ref={ref}
      className={_className}
      showOverlay={showOverlay}
      render={isMounted && (isOpen || isOpenInternal) && !isClosing}
      {...rest}
      tabIndex={-1}
    />
  )
})

if (__DEV__) {
  DialogOverlay.displayName = 'DialogOverlay'
}

export const DialogHeader = React.forwardRef((props, ref) => {
  const { className, ...rest } = props

  const { headerId, setHeaderMounted } = useDialog()

  React.useEffect(() => {
    setHeaderMounted(true)
    return () => setHeaderMounted(false)
  }, [setHeaderMounted])

  const _className = mergeClassnames('mote-dialog__header', className)

  return (
    <DialogHeaderRoot
      layout='horizontal'
      align='center'
      {...rest}
      as='header'
      ref={ref}
      className={_className}
      id={headerId}
    />
  )
})

if (__DEV__) {
  DialogHeader.displayName = 'DialogHeader'
}

export const DialogContent = React.forwardRef((props, ref) => {
  const { className, ...rest } = props
  const { setContentMounted, getDialogContentProps } = useDialog()

  const contentProps = getDialogContentProps(rest, ref)

  React.useEffect(() => {
    setContentMounted(true)
    return () => setContentMounted(false)
  }, [setContentMounted])

  const _className = mergeClassnames('mote-dialog__content', className)

  return (
    <DialogContentRoot {...contentProps} ref={ref} className={_className} />
  )
})

if (__DEV__) {
  DialogContent.displayName = 'DialogContent'
}

export const DialogFooter = React.forwardRef((props, ref) => {
  const { className, layout = 'horizontal', ...rest } = props
  const _className = mergeClassnames('mote-dialog__footer', className)

  return (
    <DialogFooterRoot
      align='center'
      justify='flex-end'
      gap={12}
      {...rest}
      as='footer'
      ref={ref}
      className={_className}
      layout={layout}
    />
  )
})

if (__DEV__) {
  DialogFooter.displayName = 'DialogFooter'
}

export const DialogCloseButton = React.forwardRef((props, ref) => {
  const { className, ...rest } = props
  const { onClose } = useDialog()

  const _className = mergeClassnames('mote-dialog__close-button', className)

  return (
    <DialogCloseButtonRoot
      ref={ref}
      size={32}
      className={_className}
      onClick={event => {
        event.stopPropagation()
        onClose('button')
      }}
      {...rest}
    />
  )
})

if (__DEV__) {
  DialogCloseButton.displayName = 'DialogCloseButton'
}

/**
 * @Deprecated
 * Heavily based on Chakra UI Modal component.
 * It provides context and functionality for all dialog components
 *
 * @see Docs     https://chakra-ui.com/docs/components/overlay/modal
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/main/packages/modal/src/modal.tsx
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices/#dialog_modal
 */
export const Dialog = props => {
  const {
    portalProps,
    children: _children,
    initialFocusRef,
    finalFocusRef,
    preserveScrollBarGap,
    focusGroup,
    showCloseButton = true,
    closeOnButtonClick = true,
    showOverlay = true,
    lockFocusAcrossFrames = true,
    returnFocusOnClose = true,
    trapFocus = true,
    autoFocus = true,
    blockScrollOnMount = true,
    allowPinchZoom = false,
    containerProps = {},
    overlayProps = {},
    closeButtonProps = {}
  } = props
  const { containerRef, ...restContainerProps } = containerProps || {}
  const { overlayRef, ...restOverlayProps } = overlayProps || {}
  const { closeButtonRef, ...restCloseButtonProps } = closeButtonProps || {}

  const dialog = useDialogFunctions(props)

  const context = {
    ...dialog,
    autoFocus,
    trapFocus,
    focusGroup,
    initialFocusRef,
    finalFocusRef,
    returnFocusOnClose,
    blockScrollOnMount,
    allowPinchZoom,
    preserveScrollBarGap,
    lockFocusAcrossFrames
  }

  const children = runIfFn(_children, {
    isOpen: context.isOpen,
    isClosing: context.isClosing,
    onClose: context.onClose
  })

  return (
    <DialogProvider value={context}>
      {(context.isOpen || context.isClosing) && (
        <Portal {...portalProps}>
          <DialogOverlay
            ref={overlayRef}
            {...restOverlayProps}
            showOverlay={showOverlay}
          />
          <DialogContainer ref={containerRef} {...restContainerProps}>
            {showCloseButton && closeOnButtonClick && (
              <DialogCloseButton
                ref={closeButtonRef}
                {...restCloseButtonProps}
              />
            )}
            {children}
          </DialogContainer>
        </Portal>
      )}
    </DialogProvider>
  )
}

if (__DEV__) {
  Dialog.displayName = 'Dialog'
}

const DialogOverlayRoot = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  opacity: 0;
  visibility: hidden;
  background: ${({ showOverlay }) =>
    showOverlay ? th.color('blackAlpha.600') : null};
  transition-property: background, opacity, visibility;
  transition-duration: ${DIALOG_TRANSITION_DURATION}ms;
  transition-timing-function: ease-in-out;

  ${({ render }) =>
    render &&
    css`
      opacity: 1;
      visibility: visible;
    `}
`

const DialogContainerRoot = styled.div`
  display: flex;
  z-index: 10000;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  align-items: ${({ isCentered }) => (isCentered ? 'center' : 'flex-start')};
  overflow: ${({ scrollBehavior }) =>
    scrollBehavior === 'inside' ? 'hidden' : 'auto'};

  @supports (height: -webkit-fill-available) {
    height: -webkit-fill-available;
  }

  > * {
    font-family: sans !important;
  }
`

const DialogRoot = styled.section`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  outline: 0;
  border-radius: 8px;
  background: white;
  color: inherit;
  margin: 60px 24px;
  z-index: 10000;
  box-shadow: lg;
  max-height: ${({ scrollBehavior }) =>
    scrollBehavior === 'inside' ? 'calc(100% - 120px)' : null};
  transform: scale(0.95);
  transition: transform ${DIALOG_TRANSITION_DURATION}ms ease-in-out;

  ${({ maxWidth }) => {
    if (maxWidth === 'full') {
      return css`
        max-width: 100vw;
        min-height: 100vh;
        @supports (min-height: -webkit-fill-available) {
          min-height: -webkit-fill-available;
        }
        margin: 0;
      `
    }

    return css`
      max-width: ${isNumber(maxWidth) ? `${maxWidth}px` : maxWidth};
    `
  }}

  ${({ render }) =>
    render &&
    css`
      transform: scale(1);
    `}
`

const DrawerContainerRoot = styled.div`
  display: flex;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  z-index: 10000;
  position: fixed;
  left: 0;
  top: 0;

  > * {
    font-family: sans !important;
  }
`

const DrawerRoot = styled.section`
  display: flex;
  flex-direction: column;
  position: fixed;
  width: 100%;
  outline: 0;
  z-index: 10000;
  max-height: 100vh;
  background: white;
  color: inherit;
  box-shadow: lg;
  transition: transform 0.2s ease-in-out;

  ${({ fullHeight }) =>
    fullHeight &&
    css`
      height: 100vh;
    `}

  ${({ maxWidth }) => {
    if (maxWidth === 'full') {
      return css`
        max-width: 100vw;
        min-height: 100vh;
      `
    }

    return css`
      max-width: ${isNumber(maxWidth) ? `${maxWidth}px` : maxWidth};
    `
  }}

  ${({ placement }) => {
    switch (placement) {
      case 'top':
        return css`
          top: 0;
          left: 0;
          right: 0;
          max-width: 100vw;
          transform: translate(0, -100%);
        `
      case 'bottom':
        return css`
          bottom: 0;
          left: 0;
          right: 0;
          max-width: 100vw;
          transform: translate(0, 100%);
        `
      case 'right':
        return css`
          left: 0;
          top: 0;
          bottom: 0;
          width: 100%;
          transform: translate(100%, 0);
        `
      case 'left':
      default:
        return css`
          left: 0;
          top: 0;
          bottom: 0;
          width: 100%;
          transform: translate(-100%, 0);
        `
    }
  }}

  ${({ render }) =>
    render &&
    css`
      transform: translate(0, 0);
    `}
`

const DialogHeaderRoot = styled(Block)`
  flex: 0 1 0;
  padding: 24px 24px 16px;
  font-size: 18px;
  line-height: 120%;
  font-weight: 500;
  color: black;
`

const DialogContentRoot = styled.div`
  flex: 1 1 0%;
  padding: 8px 24px;
  overflow: ${({ scrollBehavior }) =>
    scrollBehavior === 'inside' ? 'auto' : null};

  button.mote-dialog__close-button + & {
    padding-top: 48px;
  }

  &:first-child {
    padding-top: 24px;
  }

  &:last-child {
    padding-bottom: 24px;
  }
`

const DialogFooterRoot = styled(Block)`
  padding: 16px 24px 24px;
`

const DialogCloseButtonRoot = styled(CloseButton)`
  position: absolute;
  top: 8px;

  ${({ placement }) => {
    switch (placement) {
      case 'left':
        return css`
          left: 8px;
        `
      case 'right':
      default:
        return css`
          right: 8px;
        `
    }
  }}
`

Dialog.Overlay = DialogOverlay
Dialog.Container = DialogContainer
Dialog.CloseButton = DialogCloseButton
Dialog.Header = DialogHeader
Dialog.Content = DialogContent
Dialog.Footer = DialogFooter
